import React, { MouseEvent, ReactNode, useMemo } from "react";
import { Box, SxProps } from "@mui/system";
// @ts-ignore
import logo from "./decodable_logo_white.svg";
import { useMediaQuery } from "@mui/material";
import Link from "decodable-ui/lib/components/Link";
import { Footer } from "./Footer";
import { FadeTransition } from "./FadeTransition";
import { DetailCard } from "./DetailCard";
import { PlanSelector } from "./PlanSelector";
import { Plan, searchParamNames } from "../constants";
import { useSearchParams } from "react-router-dom";

type Areas =
  | "heroBackdrop"
  | "heroTitle"
  | "heroDescription"
  | "details"
  | "detailCards"
  | "bottomSpacer"
  | "form"
  | "footer";

type Layout = {
  grid: { sx: SxProps };
  areas: {
    [key in Areas]: { sx: SxProps };
  };
};

function useLayout(): Layout {
  const isNarrowScreen = useMediaQuery("(max-width:800px)");
  const isWideScreen = useMediaQuery("(min-width:162ch)");

  return {
    grid: {
      sx: {
        minWidth: "60ch",
        maxWidth: "100vw",
        overflow: "hidden",

        display: "grid",
        // Left and right columns are just spacers. The inner 2 contain the description and form
        gridTemplateColumns: `1fr minmax(auto, 1200px) ${
          isWideScreen ? "auto" : ""
        } 1fr`,
        gridTemplateRows: "auto auto auto 1fr auto",
        minHeight: "100vh",

        gridTemplateAreas: isWideScreen
          ? `
        'lpad title title rpad'
        'lpad desc  form  rpad'
        'lpad deet  form  rpad'
        'lpad bpad  bpad  rpad'
        'foot foot  foot  foot'
      `
          : `
        'lpad title rpad'
        'lpad desc  rpad'
        'lpad form  rpad'
        'lpad deet  rpad'
        'lpad bpad  rpad'
        'foot foot  foot'
      `,

        "& h1": isWideScreen
          ? { fontSize: "4rem", margin: 0 }
          : { fontSize: "2.5rem", margin: 0 },
        "& h2": isWideScreen
          ? { fontSize: "2rem", margin: 0, marginBottom: "1rem" }
          : { fontSize: "2rem", margin: 0, marginBottom: "1rem" },
        "& h3": {
          fontSize: isNarrowScreen ? "1.2rem" : "1.4rem",
        },
      },
    },
    areas: {
      heroBackdrop: {
        sx: {
          gridArea: "title-start / lpad-start / desc-end / rpad-end",
          bgcolor: "var(--de-color-corp-dark-blue)",
          backgroundImage: `
          radial-gradient(circle at top 20ch left 50ch, rgb(100%, 100%, 100%, 2%) 40%, transparent),
          radial-gradient(circle at top 0 right 0, transparent 298px, var(--de-color-plum) 300px, var(--de-color-plum) 310px, transparent 312px)
        `,
        },
      },
      heroTitle: {
        sx: {
          gridArea: "title",
          color: "white",
        },
      },
      heroDescription: {
        sx: {
          gridArea: "desc",
          color: "white",
          padding: isWideScreen ? "0 4rem 1rem" : "0 4rem 6rem",
          "& p": {
            fontSize: "1.25rem",
          },
          minHeight: "14em",
        },
      },
      form: {
        sx: {
          width: isWideScreen ? "60ch" : "min(80ch, 80%)",
          gridArea: "form",
          placeSelf: "start center",
          margin: `${isWideScreen ? "2rem" : "-4rem"} 6ch 2rem`,
        },
      },
      details: {
        sx: {
          gridArea: "deet",
          placeSelf: "start stretch",
          justifyContent: "center",
          width: "100%",
          maxWidth: "110ch",

          marginTop: "1rem",
          marginX: "auto",
          "& h2": {
            margin: 0,
            textAlign: "center",
            padding: "2ch 2ch 0",
          },
        },
      },
      detailCards: {
        sx: {
          display: "grid",
          margin: "2rem",
          gap: "2ch",
          gridTemplateColumns: "repeat(auto-fill, 30ch)",
          gridAutoFlow: "row",
          justifyContent: "center",
          placeItems: "stretch",

          "& .detail-card": {},
          "& .detail-card-title": {
            fontSize: "1rem",
            fontWeight: "600",
          },
          "& .detail-card-description": {
            fontSize: "0.9rem",
            color: "text.secondary",
          },
        },
      },
      bottomSpacer: {
        sx: {
          gridArea: "bpad",
        },
      },
      footer: {
        sx: {
          gridArea: "foot",
        },
      },
    },
  };
}

export function AccountCreatePage({
  plan,
  onSelectPlan,
  form,
}: {
  plan: Plan;
  onSelectPlan: (id: Plan["id"]) => void;
  form: JSX.Element;
}) {
  const layout = useLayout();

  // Memoized to ensure the transition effect only triggers when this actually changes.
  const description = useMemo(
    () => (
      <>
        {" "}
        {planInfo[plan.id].description}
        {planInfo[plan.id].switchPlanCTA}
      </>
    ),
    [plan]
  );

  return (
    <Box sx={layout.grid.sx}>
      <Box sx={layout.areas.heroBackdrop.sx} />
      <HeroSection sx={layout.areas.heroTitle.sx} plan={plan} />
      <Box sx={{ ...layout.areas.heroDescription.sx }}>
        <FadeTransition>{description}</FadeTransition>
      </Box>
      <Box sx={layout.areas.details.sx}>
        <Box
          component="h3"
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "2ch",
            alignItems: "center",
            margin: "1ch",
          }}
        >
          <Box>Pick the plan that's right for you</Box>
          <PlanSelector selectedPlan={plan} onSelectPlan={onSelectPlan} />
        </Box>
        <Box sx={layout.areas.detailCards.sx}>
          <DetailCard
            isFree
            selectedPlan={plan}
            title="Pre-Built Connector Library"
            description="Use pre-built connectors to get data from databases and event streaming platforms in real-time. Send that data to the destination where it has the most value."
          />
          <DetailCard
            isFree
            selectedPlan={plan}
            title="Change Data Capture with Debezium"
            description="Replicate tables from OLTP database systems using connectors with Debezium-powered Change Data Capture (CDC) support."
          />
          <DetailCard
            isFree
            selectedPlan={plan}
            title="Custom role-based access control"
            description="Manage who has access to your Decodable resources and what they can do with them."
          />
          <DetailCard
            isFree
            selectedPlan={plan}
            title="Basic or intermediate transformations"
            description="Filter, route, extract, aggregate, and enrich data using the SQL that you already know."
          />
          <DetailCard
            isFree={false}
            selectedPlan={plan}
            title="Advanced transformations"
            description="Perform stream-stream joins to combine two or more streams into one enriched stream. Or, write code in a JVM-based language to do even more advanced processing."
          />
          <DetailCard
            isFree={false}
            selectedPlan={plan}
            title="Enhanced support"
            description="Get direct help via Slack or Email with a Decodable professional."
          />
        </Box>
      </Box>
      <Box
        sx={{
          ...layout.areas.bottomSpacer.sx,
          bgcolor: "background.default",
        }}
      />
      <Box sx={layout.areas.form.sx}>{form}</Box>
      <Footer sx={layout.areas.footer.sx} />
    </Box>
  );
}

function PlanLink({
  planId,
  children,
}: {
  planId: Plan["id"];
  children: ReactNode;
}) {
  const [params, setParams] = useSearchParams();

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    params.set(searchParamNames.selectedPlanId, planId);
    setParams(params, { replace: true });
  }

  return (
    <Link
      sx={{ fontWeight: "700" }}
      href={`?selected_plan_id=${planId}`}
      onClick={handleClick}
    >
      {children}
    </Link>
  );
}

const planInfo: {
  [key in "free" | "on_demand"]: {
    h2: ReactNode;
    switchPlanCTA: ReactNode;
    description: ReactNode;
  };
} = {
  free: {
    h2: "No credit card required. Start free.",
    description: (
      <>
        <Box component={"p"}>
          Developer accounts can use 4 free tasks for running connections and
          pipelines.
        </Box>
      </>
    ),
    switchPlanCTA: (
      <Box component={"p"}>
        Ready for more? Start with a{" "}
        <PlanLink planId="on_demand">Team account</PlanLink> instead, or upgrade
        any time.
      </Box>
    ),
  },
  on_demand: {
    h2: "Use as much as you'd like. Pay only for what you use.",
    description: (
      <>
        <Box component={"p"}>
          Team accounts have unlimited tasks for running connections and
          pipelines.
        </Box>
        <Box component={"p"}>
          Pay-as-you-go, so you can scale your data processing workloads while
          only paying for what you actually use.
        </Box>
      </>
    ),
    switchPlanCTA: (
      <Box component={"p"}>
        Want to try it out first or process low amounts of data? Start with a
        free <PlanLink planId="free">Developer account</PlanLink>. You can
        upgrade any time.
      </Box>
    ),
  },
};

function HeroSection({ sx, plan }) {
  const cta = useMemo(
    () => (
      <>
        <Box component={"h1"}>
          Get started with the <u>{plan.name}</u> plan
        </Box>
        <Box component={"h2"}>{planInfo[plan.id].h2}</Box>
      </>
    ),
    [plan]
  );
  return (
    <Box
      className="hero"
      sx={{
        ...sx,
        padding: "4rem 4rem 0",
      }}
    >
      <img src={logo} width="200" height="30" style={{ marginBottom: "2ch" }} />
      <FadeTransition>{cta}</FadeTransition>
    </Box>
  );
}
